import { useEffect, useState } from "react";
import { Highlighter } from "lucide-react";

/**
 * Two student-controlled toggles that ride alongside every practice module:
 *   - Word highlight: green highlight over every correctly typed word.
 *   - Mistake highlight: red underline over every incorrectly typed word.
 * Persisted to localStorage so the preference sticks between sessions.
 */
export const DEFAULT_HIGHLIGHT_PREFS = {
  wordHighlight: true,
  mistakeHighlight: true,
  autoScroll: true,
};
const STORAGE_KEY = "stenosync.highlightPrefs.v1";

export function loadHighlightPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_HIGHLIGHT_PREFS };
    return { ...DEFAULT_HIGHLIGHT_PREFS, ...JSON.parse(raw) };
  } catch { return { ...DEFAULT_HIGHLIGHT_PREFS }; }
}

function save(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

const TOGGLES = [
  { id: "wordHighlight",     label: "Word highlight",    hint: "Green tint on every correctly typed word" },
  { id: "mistakeHighlight",  label: "Mistake underline", hint: "Red wavy underline on every wrong word"   },
  { id: "autoScroll",        label: "Auto scroll",       hint: "Auto-scroll the source so the current word stays in view" },
];

export default function HighlightControlsPanel({ value, onChange, testId = "highlight-controls" }) {
  const [local, setLocal] = useState(value || DEFAULT_HIGHLIGHT_PREFS);
  useEffect(() => { setLocal(value || DEFAULT_HIGHLIGHT_PREFS); }, [value]);

  const toggle = (id) => {
    const next = { ...local, [id]: !local[id] };
    setLocal(next);
    save(next);
    onChange?.(next);
  };

  return (
    <div
      className="flex flex-wrap items-center gap-2 border border-border rounded-lg bg-secondary/40 px-3 py-2"
      data-testid={testId}
    >
      <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold">
        <Highlighter className="w-3.5 h-3.5" /> Highlights
      </span>
      {TOGGLES.map((t) => {
        const on = !!local[t.id];
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => toggle(t.id)}
            data-testid={`${testId}-${t.id}`}
            aria-pressed={on}
            title={t.hint}
            className={`inline-flex items-center gap-2 h-8 px-3 rounded-md border transition-colors ${
              on
                ? "border-brand-gold bg-brand-gold text-background"
                : "border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-brand-gold/40"
            }`}
          >
            <span
              className={`inline-flex w-7 h-4 rounded-full transition-colors ${
                on ? "bg-background/40" : "bg-secondary"
              }`}
            >
              <span
                className={`inline-block w-3 h-3 bg-white rounded-full mt-0.5 transition-transform ${
                  on ? "translate-x-3.5" : "translate-x-0.5"
                }`}
              />
            </span>
            <span className="text-[10px] uppercase tracking-widest font-bold">
              {t.label} · {on ? "ON" : "OFF"}
            </span>
          </button>
        );
      })}
    </div>
  );
}
